<template>
  <div
    :class="[
      ns.b(),
      ns.m(numericInputSize),
      ns.is('disabled', numericInputDisabled),
      ns.is('without-controls', !controls),
      ns.is('controls-right', controlsAtRight),
    ]"
    data-test-name="numeric-input"
    data-test-variant=""
    data-test-state=""
    @dragstart.prevent
  >
    <b-input
      :id="id"
      ref="input"
      type="number"
      :step="step"
      :model-value="displayValue"
      :placeholder="placeholder"
      :disabled="numericInputDisabled"
      :size="numericInputSize"
      :max="max"
      :min="min"
      :name="name"
      :label="label"
      :validate-event="false"
      prefix-class="is-button"
      suffix-class="is-button"
      @keydown.up.prevent="increase"
      @keydown.down.prevent="decrease"
      @blur="handleBlur"
      @focus="handleFocus"
      @input="handleInput"
      @change="handleInputChange"
    >
      <template v-if="controls && !controlsAtRight" #prefix>
        <b-button
          v-repeat-click="decrease"
          ghost
          :small="numericInputSize === 'large'"
          :xsmall="!['large', 'small'].includes(numericInputSize)"
          :xxsmall="numericInputSize === 'small'"
          :disabled="numericInputDisabled"
          :aria-label="t('b.numericInput.decrease')"
          :class="[ns.e('decrease'), ns.is('disabled', minDisabled)]"
          :icon="ControlMinus"
          @keydown.enter="decrease"
        />
      </template>
      <template v-if="controls" #suffix>
        <div v-if="controlsAtRight" :class="ns.e('controls-right')">
          <b-button
            v-repeat-click="increase"
            ghost
            :xsmall="numericInputSize === 'large'"
            :xxsmall="numericInputSize !== 'large'"
            :disabled="numericInputDisabled"
            :aria-label="t('b.numericInput.increase')"
            :class="[ns.e('increase'), ns.is('disabled', maxDisabled)]"
            :icon="ChevronUp"
            @keydown.enter="increase"
          />
          <b-button
            v-repeat-click="decrease"
            ghost
            :xsmall="numericInputSize === 'large'"
            :xxsmall="numericInputSize !== 'large'"
            :disabled="numericInputDisabled"
            :aria-label="t('b.numericInput.decrease')"
            :class="[ns.e('decrease'), ns.is('disabled', minDisabled)]"
            :icon="ChevronDown"
            @keydown.enter="decrease"
          />
        </div>
        <b-button
          v-else
          v-repeat-click="increase"
          ghost
          :small="numericInputSize === 'large'"
          :xsmall="!['large', 'small'].includes(numericInputSize)"
          :xxsmall="numericInputSize === 'small'"
          :disabled="numericInputDisabled"
          :aria-label="t('b.numericInput.increase')"
          :class="[ns.e('increase'), ns.is('disabled', maxDisabled)]"
          :icon="ControlPlus"
          @keydown.enter="increase"
        />
      </template>
    </b-input>
  </div>
</template>
<script lang="ts" setup>
import { computed, onMounted, onUpdated, reactive, ref, watch } from 'vue'
import { isNil } from 'lodash-unified'
import { BInput } from '@bigin/components/input'
import { BButton } from '@bigin/components/button'
import { vRepeatClick } from '@bigin/directives'
import {
  useDisabled,
  // useFormItem,
  useLocale,
  useNamespace,
  useSize,
} from '@bigin/hooks'
import { debugWarn, isNumber, isString, isUndefined } from '@bigin/utils'
import {
  ChevronDown,
  ChevronUp,
  ControlMinus,
  ControlPlus,
} from '@bigin/icons-vue'
import { numericInputEmits, numericInputProps } from './numeric-input'
import type { InputInstance } from '@bigin/components/input'

defineOptions({
  name: 'BNumericInput',
})

const props = defineProps(numericInputProps)
const emit = defineEmits(numericInputEmits)

const { t } = useLocale()
const ns = useNamespace('numeric-input')
const input = ref<InputInstance>()

interface Data {
  currentValue: number | null | undefined
  userInput: null | number | string
}
const data = reactive<Data>({
  currentValue: props.modelValue,
  userInput: null,
})

// const { formItem } = useFormItem()

const minDisabled = computed(
  () =>
    isNumber(props.modelValue) &&
    ensurePrecision(props.modelValue, -1)! < props.min
)
const maxDisabled = computed(
  () =>
    isNumber(props.modelValue) && ensurePrecision(props.modelValue)! > props.max
)

const numPrecision = computed(() => {
  const stepPrecision = getPrecision(props.step)
  if (!isUndefined(props.precision)) {
    if (stepPrecision > props.precision) {
      debugWarn(
        'NumericInput',
        'precision should not be less than the decimal places of step'
      )
    }
    return props.precision
  } else {
    return Math.max(getPrecision(props.modelValue), stepPrecision)
  }
})
const controlsAtRight = computed(() => {
  return props.controls && props.controlsPosition === 'right'
})

const numericInputSize = useSize()
const numericInputDisabled = useDisabled()

const displayValue = computed(() => {
  if (data.userInput !== null) {
    return data.userInput
  }
  let currentValue: number | string | undefined | null = data.currentValue
  if (isNil(currentValue)) return ''
  if (isNumber(currentValue)) {
    if (Number.isNaN(currentValue)) return ''
    if (!isUndefined(props.precision)) {
      currentValue = currentValue.toFixed(props.precision)
    }
  }
  return currentValue
})
const toPrecision = (num: number, pre?: number) => {
  if (isUndefined(pre)) pre = numPrecision.value
  if (pre === 0) return Math.round(num)
  let snum = String(num)
  const pointPos = snum.indexOf('.')
  if (pointPos === -1) return num
  const nums = snum.replace('.', '').split('')
  const datum = nums[pointPos + pre]
  if (!datum) return num
  const length = snum.length
  if (snum.charAt(length - 1) === '5') {
    snum = `${snum.slice(0, Math.max(0, length - 1))}6`
  }
  return Number.parseFloat(Number(snum).toFixed(pre))
}
const getPrecision = (value: number | null | undefined) => {
  if (isNil(value)) return 0
  const valueString = value.toString()
  const dotPosition = valueString.indexOf('.')
  let precision = 0
  if (dotPosition !== -1) {
    precision = valueString.length - dotPosition - 1
  }
  return precision
}
const ensurePrecision = (val: number, coefficient: 1 | -1 = 1) => {
  if (!isNumber(val)) return data.currentValue
  // Solve the accuracy problem of JS decimal calculation by converting the value to integer.
  return toPrecision(val + props.step * coefficient)
}
const increase = () => {
  if (numericInputDisabled.value || maxDisabled.value) return
  const value = props.modelValue || 0
  const newVal = ensurePrecision(value)
  setCurrentValue(newVal)
}
const decrease = () => {
  if (numericInputDisabled.value || minDisabled.value) return
  const value = props.modelValue || 0
  const newVal = ensurePrecision(value, -1)
  setCurrentValue(newVal)
}
const verifyValue = (
  value: number | string | null | undefined,
  update?: boolean
): number | null | undefined => {
  const { max, min, step, precision, stepStrictly, valueOnClear } = props
  let newVal = Number(value)
  if (isNil(value) || Number.isNaN(newVal)) {
    return null
  }
  if (value === '') {
    if (valueOnClear === null) {
      return null
    }
    newVal = isString(valueOnClear) ? { min, max }[valueOnClear] : valueOnClear
  }
  if (stepStrictly) {
    newVal = Math.round(newVal / step) * step
  }
  if (!isUndefined(precision)) {
    newVal = toPrecision(newVal, precision)
  }
  if (newVal > max || newVal < min) {
    newVal = newVal > max ? max : min
    update && emit('update:modelValue', newVal)
  }
  return newVal
}
const setCurrentValue = (value: number | string | null | undefined) => {
  const oldVal = data.currentValue
  const newVal = verifyValue(value)
  if (oldVal === newVal) return
  data.userInput = null
  emit('update:modelValue', newVal!)
  emit('input', newVal)
  emit('change', newVal!, oldVal!)
  data.currentValue = newVal
}
const handleInput = (value: string) => {
  return (data.userInput = value)
}
const handleInputChange = (value: string) => {
  const newVal = value !== '' ? Number(value) : ''
  if ((isNumber(newVal) && !Number.isNaN(newVal)) || value === '') {
    setCurrentValue(newVal)
  }
  data.userInput = null
}

const focus = () => {
  input.value?.focus?.()
}

const blur = () => {
  input.value?.blur?.()
}

const handleFocus = (event: MouseEvent | FocusEvent) => {
  emit('focus', event)
}

const handleBlur = (event: MouseEvent | FocusEvent) => {
  emit('blur', event)
}

watch(
  () => props.modelValue,
  (value) => {
    data.currentValue = verifyValue(value, true)
    data.userInput = null
  },
  { immediate: true }
)

onMounted(() => {
  const { min, max, modelValue } = props
  const innerInput = input.value?.input as HTMLInputElement
  innerInput.setAttribute('role', 'spinbutton')
  if (Number.isFinite(max)) {
    innerInput.setAttribute('aria-valuemax', String(max))
  } else {
    innerInput.removeAttribute('aria-valuemax')
  }
  if (Number.isFinite(min)) {
    innerInput.setAttribute('aria-valuemin', String(min))
  } else {
    innerInput.removeAttribute('aria-valuemin')
  }
  innerInput.setAttribute('aria-valuenow', String(data.currentValue))
  innerInput.setAttribute('aria-disabled', String(numericInputDisabled.value))
  if (!isNumber(modelValue) && modelValue != null) {
    let val: number | null = Number(modelValue)
    if (Number.isNaN(val)) {
      val = null
    }
    emit('update:modelValue', val!)
  }
})

onUpdated(() => {
  const innerInput = input.value?.input
  innerInput?.setAttribute('aria-valuenow', `${data.currentValue}`)
})

defineExpose({
  /** @description get focus the input component */
  focus,
  /** @description remove focus the input component */
  blur,
})
</script>
